import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { AnimatePresence, motion } from "framer-motion";
import {
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Flag,
  Monitor,
  Play,
  RefreshCw,
  Smartphone,
} from "lucide-react";

import { TaggingAnnotationOverlay } from "./TaggingAnnotationOverlay";
import { TaggingQuestionsPanel } from "./TaggingQuestionsPanel";
import {
  resolvePrototypeIterationIdFromDetail,
  useFeedbackFlowDeploymentStatus,
  usePrototypeByExperiment,
} from "../../api/experimentsApi";
import {
  buildExperimentPreviewUrl,
  normalizeExperimentPreviewRoute,
} from "../../config/previewUrl";
import { useExperimentEditor } from "../../hooks/useExperimentEditor";
import { useGenerateSurveyQuestions } from "../../hooks/useGenerateSurveyQuestions";
import { useTaggingFeedbackFlow } from "../../hooks/useTaggingFeedbackFlow";
import { formatExperimentStatusLabel } from "../../utils/experimentStatus";
import { getQuestionsBySection } from "../../utils/feedbackFlow";
import { isMessageFromPreviewFrame } from "../../utils/previewMessaging";
import { ParticipantPreviewContentScreen } from "../preview/ParticipantPreviewContentScreen";
import { PersistedExperimentParticipantPreview } from "../preview/PersistedExperimentParticipantPreview";

import type { CoreResearchQuestionOption, TaggingMode } from "./tagging.types";
import type {
  ExperimentFormData,
  FeedbackFlowContent,
} from "../../types/experiment.types";

const SITEMAP_CANDIDATE_FILES = ["sitemap.xml", "sitemap_index.xml", "sitemap-index.xml"];
const MAX_SITEMAP_FILES = 8;
const MAX_ROUTES_FROM_SITEMAP = 30;

type PreviewMode = "intro" | "product" | "outro";

function normalizeRouteFromAnyUrl(
  rawRoute: string,
  previewBaseUrl?: string,
): string {
  return normalizeExperimentPreviewRoute(rawRoute, previewBaseUrl);
}

function getSitemapUrlsFromPreviewBase(previewBaseUrl: string): string[] {
  try {
    const parsed = /^https?:\/\//i.test(previewBaseUrl)
      ? new URL(previewBaseUrl)
      : new URL(previewBaseUrl, window.location.origin);
    return SITEMAP_CANDIDATE_FILES.map((fileName) =>
      new URL(fileName, parsed).toString(),
    );
  } catch {
    return [];
  }
}

function extractSitemapLocs(xmlDoc: Document, baseUrl: string): {
  pageUrls: string[];
  childSitemapUrls: string[];
} {
  const pageUrls: string[] = [];
  const childSitemapUrls: string[] = [];
  const locNodes = Array.from(xmlDoc.getElementsByTagNameNS("*", "loc"));

  for (const locNode of locNodes) {
    const rawLoc = locNode.textContent?.trim();
    if (!rawLoc) {
      continue;
    }

    let absoluteUrl = "";
    try {
      absoluteUrl = new URL(rawLoc, baseUrl).toString();
    } catch {
      continue;
    }

    const parentTag = locNode.parentElement?.localName?.toLowerCase();
    if (parentTag === "url") {
      pageUrls.push(absoluteUrl);
      continue;
    }
    if (parentTag === "sitemap") {
      childSitemapUrls.push(absoluteUrl);
    }
  }

  return { pageUrls, childSitemapUrls };
}

async function fetchRoutesFromSitemap(
  sitemapUrls: string[],
  signal: AbortSignal,
  previewBaseUrl?: string,
): Promise<string[]> {
  const visitedSitemaps = new Set<string>();
  const pendingSitemaps = [...sitemapUrls];
  const routeSet = new Set<string>();
  const routes: string[] = [];

  while (pendingSitemaps.length > 0 && visitedSitemaps.size < MAX_SITEMAP_FILES) {
    const sitemapUrl = pendingSitemaps.shift();
    if (!sitemapUrl || visitedSitemaps.has(sitemapUrl)) {
      continue;
    }
    visitedSitemaps.add(sitemapUrl);

    let xmlText = "";
    try {
      const response = await fetch(sitemapUrl, {
        signal,
        headers: {
          Accept: "application/xml, text/xml, */*",
        },
      });
      if (!response.ok) {
        continue;
      }
      xmlText = await response.text();
    } catch {
      continue;
    }

    const xmlDoc = new DOMParser().parseFromString(xmlText, "application/xml");
    if (xmlDoc.getElementsByTagName("parsererror").length > 0) {
      continue;
    }

    const { pageUrls, childSitemapUrls } = extractSitemapLocs(xmlDoc, sitemapUrl);
    for (const childSitemapUrl of childSitemapUrls) {
      if (!visitedSitemaps.has(childSitemapUrl) && pendingSitemaps.length < MAX_SITEMAP_FILES) {
        pendingSitemaps.push(childSitemapUrl);
      }
    }

    for (const pageUrl of pageUrls) {
      const route = normalizeRouteFromAnyUrl(pageUrl, previewBaseUrl);
      if (routeSet.has(route)) {
        continue;
      }
      routeSet.add(route);
      routes.push(route);
      if (routes.length >= MAX_ROUTES_FROM_SITEMAP) {
        return routes;
      }
    }
  }

  return routes;
}

function normalizeDeploymentStatus(status?: string | number | null): string {
  return String(status ?? "").trim().toLowerCase();
}

const IntroMockup = ({
  intro,
  setActivePreview,
}: {
  intro: FeedbackFlowContent;
  setActivePreview: (value: PreviewMode) => void;
}) => (
  <div className="absolute inset-0 overflow-y-auto bg-[radial-gradient(circle_at_top,rgba(217,182,75,0.12),transparent_28%),linear-gradient(180deg,#080808_0%,#020202_100%)]">
    <ParticipantPreviewContentScreen
      eyebrow="Front Matter"
      title="Welcome"
      description={intro.description}
      terms={intro.terms}
      actions={
        <button
          type="button"
          onClick={() => setActivePreview("product")}
          className="rounded-lg bg-[#D9B64B] px-4 py-2 text-sm font-medium text-black transition-colors hover:bg-[#E7C96C]"
        >
          Begin Study
        </button>
      }
    />
  </div>
);

const OutroMockup = ({
  outro,
  setActivePreview,
}: {
  outro: FeedbackFlowContent;
  setActivePreview: (value: PreviewMode) => void;
}) => (
  <div className="absolute inset-0 overflow-y-auto bg-[radial-gradient(circle_at_top,rgba(217,182,75,0.12),transparent_28%),linear-gradient(180deg,#080808_0%,#020202_100%)]">
    <ParticipantPreviewContentScreen
      eyebrow="End Matter"
      title="Thank You"
      description={outro.description}
      terms={outro.terms}
      centered
      actions={
        <button
          type="button"
          onClick={() => setActivePreview("product")}
          className="rounded-lg border border-white/10 px-4 py-2 text-sm text-slate-300 transition-colors hover:bg-white/5"
        >
          Back to Product
        </button>
      }
    />
  </div>
);

export const ExperimentTaggingWorkspace: React.FC<{
  experimentData: ExperimentFormData;
  setExperimentData: React.Dispatch<React.SetStateAction<ExperimentFormData>>;
  coreResearchQuestions: CoreResearchQuestionOption[];
}> = ({ experimentData, setExperimentData, coreResearchQuestions }) => {
  const { experimentStatus, isExperimentViewOnly } = useExperimentEditor();
  const previewFrameRef = useRef<HTMLIFrameElement | null>(null);
  const experimentId =
    experimentData?.experimentId?.trim?.() || experimentData?.experimentId;
  const [taggingMode, setTaggingMode] = useState<TaggingMode>("view");
  const [taggingForQuestionId, setTaggingForQuestionId] = useState<string | null>(null);
  const [iframeRoute, setIframeRoute] = useState<string>("/");
  const [sitemapRoutes, setSitemapRoutes] = useState<string[]>([]);
  const [isSitemapLoading, setIsSitemapLoading] = useState(false);
  const [viewport, setViewport] = useState<"desktop" | "mobile">("desktop");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [activePreview, setActivePreview] = useState<PreviewMode>("product");
  const [isParticipantPreviewOpen, setIsParticipantPreviewOpen] = useState(false);
  const readOnlyMessage = useMemo(
    () =>
      `This experiment is ${formatExperimentStatusLabel(
        experimentStatus,
      ).toLowerCase()}. Feedback flow tagging is view only.`,
    [experimentStatus],
  );
  const prototypeQuery = usePrototypeByExperiment(experimentId, {
    enabled: Boolean(experimentId),
  });
  const prototypeIterationId = useMemo(
    () => resolvePrototypeIterationIdFromDetail(prototypeQuery.data),
    [prototypeQuery.data],
  );
  const prototypeDeploymentStatus = normalizeDeploymentStatus(
    prototypeQuery.data?.deploymentStatus,
  );
  const deploymentStatusQuery = useFeedbackFlowDeploymentStatus(
    experimentId,
    prototypeIterationId ?? undefined,
    {
      enabled:
        Boolean(experimentId) &&
        Boolean(prototypeIterationId) &&
        prototypeDeploymentStatus !== "deployed",
    },
  );
  const deploymentStatusData = deploymentStatusQuery.data?.data;
  const deploymentStatus = normalizeDeploymentStatus(
    deploymentStatusData?.deploymentStatus ?? prototypeQuery.data?.deploymentStatus,
  );
  const {
    feedbackFlowDraft,
    updateIntro,
    updateOutro,
    addQuestion,
    saveQuestion,
    deleteQuestion,
    reorderQuestions,
    setSectionEnabled,
    applyTaggedTarget,
    refetchFeedbackFlow,
    dismissSyncError,
    syncError,
    isFeedbackFlowInitializing,
    isFeedbackFlowSyncing,
  } = useTaggingFeedbackFlow({
    experimentData,
    setExperimentData,
    experimentId,
    iterationId: prototypeIterationId,
  });
  const previewBaseUrl =
    deploymentStatusData?.publicUrl?.trim() ||
    prototypeQuery.data?.publicUrl?.trim() ||
    undefined;
  const isTaggingPanelInitializing =
    Boolean(experimentId) &&
    (prototypeQuery.isLoading || isFeedbackFlowInitializing);
  const isPreviewReady =
    deploymentStatus === "deployed" && Boolean(previewBaseUrl);

  const {
    isGenerating,
    generationError,
    dismissGenerationError,
  } = useGenerateSurveyQuestions({
    experimentId,
    iterationId: prototypeIterationId ?? undefined,
    refetchFeedbackFlow,
    isPreviewReady,
  });

  const prototypeQuestions = useMemo(
    () => getQuestionsBySection(feedbackFlowDraft, "prototype"),
    [feedbackFlowDraft],
  );

  const handleRefresh = useCallback(() => {
    setIsRefreshing(true);
    if (isPreviewReady) {
      setRefreshKey((current) => current + 1);
    }

    void Promise.allSettled([
      prototypeQuery.refetch(),
      deploymentStatusQuery.refetch(),
      refetchFeedbackFlow(),
    ]).finally(() => {
      setTimeout(() => setIsRefreshing(false), 800);
    });
  }, [deploymentStatusQuery, isPreviewReady, prototypeQuery, refetchFeedbackFlow]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!isMessageFromPreviewFrame(event, previewFrameRef.current)) {
        return;
      }

      if (event.data?.type === "iframe-url-change") {
        setIframeRoute(
          normalizeRouteFromAnyUrl(event.data.url || "/", previewBaseUrl),
        );
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [previewBaseUrl]);

  useEffect(() => {
    if (!isPreviewReady || !previewBaseUrl) {
      setSitemapRoutes([]);
      setIsSitemapLoading(false);
      return;
    }

    let isActive = true;
    const abortController = new AbortController();
    const sitemapCandidates = getSitemapUrlsFromPreviewBase(previewBaseUrl);

    if (sitemapCandidates.length === 0) {
      setSitemapRoutes([]);
      setIsSitemapLoading(false);
      return () => abortController.abort();
    }

    const loadSitemapRoutes = async () => {
      setIsSitemapLoading(true);
      try {
        const routes = await fetchRoutesFromSitemap(
          sitemapCandidates,
          abortController.signal,
          previewBaseUrl,
        );
        if (isActive) {
          setSitemapRoutes(routes);
        }
      } finally {
        if (isActive) {
          setIsSitemapLoading(false);
        }
      }
    };

    void loadSitemapRoutes();

    return () => {
      isActive = false;
      abortController.abort();
    };
  }, [isPreviewReady, previewBaseUrl, refreshKey]);

  const handleTaggingModeChange = useCallback((mode: TaggingMode) => {
    if (isExperimentViewOnly) {
      return;
    }

    setTaggingMode(mode);
    if (mode !== "draw") {
      setTaggingForQuestionId(null);
    }
  }, [isExperimentViewOnly]);

  const handleAnnotationCreate = useCallback(
    (rect: {
      x: number;
      y: number;
      width: number;
      height: number;
      elementSelector?: string;
      createdAtScrollX?: number;
      createdAtScrollY?: number;
    }) => {
      if (isExperimentViewOnly) {
        return;
      }

      const taggedTarget = {
        route: iframeRoute,
        selector: rect.elementSelector,
        coordinates: {
          x: rect.x,
          y: rect.y,
          width: rect.width,
          height: rect.height,
          createdAtScrollX: rect.createdAtScrollX ?? 0,
          createdAtScrollY: rect.createdAtScrollY ?? 0,
        },
      };

      applyTaggedTarget(taggingForQuestionId, taggedTarget);

      setTaggingForQuestionId(null);
      setTaggingMode("view");
      setActivePreview("product");
    },
    [applyTaggedTarget, iframeRoute, isExperimentViewOnly, taggingForQuestionId],
  );

  const handleTagExistingQuestion = useCallback((questionId: string) => {
    if (isExperimentViewOnly) {
      return;
    }

    setTaggingForQuestionId(questionId);
    setTaggingMode("draw");
    setActivePreview("product");
  }, [isExperimentViewOnly]);

  const handleDeleteQuestion = useCallback(
    async (questionId: string): Promise<boolean> => {
      if (isExperimentViewOnly) {
        return false;
      }

      const didDelete = await deleteQuestion(questionId);
      setTaggingForQuestionId((current) => (current === questionId ? null : current));
      return didDelete;
    },
    [deleteQuestion, isExperimentViewOnly],
  );

  const handleParticipantPreviewFinish = useCallback(
    (
      _submissionPayload: Parameters<
        React.ComponentProps<typeof PersistedExperimentParticipantPreview>["onFinish"]
      >[0],
    ) => {
      setIsParticipantPreviewOpen(false);
    },
    [],
  );

  const fallbackRoutesFromQuestions = Array.from(
    new Set(
      prototypeQuestions
        .map((question) => question.taggedTarget?.route)
        .filter((route): route is string => Boolean(route))
        .map((route) => normalizeRouteFromAnyUrl(route, previewBaseUrl)),
    ),
  );

  const navigableRoutes =
    sitemapRoutes.length > 0 ? sitemapRoutes : fallbackRoutesFromQuestions;
  const normalizedCurrentRoute = normalizeRouteFromAnyUrl(
    iframeRoute,
    previewBaseUrl,
  );
  const activeRouteIndex = navigableRoutes.findIndex(
    (route) => route === normalizedCurrentRoute,
  );

  const goToRoute = useCallback((route: string) => {
    if (!previewFrameRef.current || !isPreviewReady) {
      return;
    }
    setActivePreview("product");
    previewFrameRef.current.src = buildExperimentPreviewUrl(route, previewBaseUrl);
    setIframeRoute(normalizeRouteFromAnyUrl(route, previewBaseUrl));
  }, [isPreviewReady, previewBaseUrl]);

  const goToPreviousRoute = useCallback(() => {
    if (activeRouteIndex <= 0) {
      return;
    }
    goToRoute(navigableRoutes[activeRouteIndex - 1]);
  }, [activeRouteIndex, goToRoute, navigableRoutes]);

  const goToNextRoute = useCallback(() => {
    if (activeRouteIndex < 0 || activeRouteIndex >= navigableRoutes.length - 1) {
      return;
    }
    goToRoute(navigableRoutes[activeRouteIndex + 1]);
  }, [activeRouteIndex, goToRoute, navigableRoutes]);

  const openPreviewInNewTab = useCallback(() => {
    if (!isPreviewReady) {
      return;
    }

    window.open(
      buildExperimentPreviewUrl(iframeRoute, previewBaseUrl),
      "_blank",
      "noopener,noreferrer",
    );
  }, [iframeRoute, isPreviewReady, previewBaseUrl]);

  const showTopRouteFlow =
    feedbackFlowDraft.intro.enabled ||
    feedbackFlowDraft.outro.enabled ||
    isSitemapLoading ||
    navigableRoutes.length > 0;
  const effectiveTaggingMode: TaggingMode = isExperimentViewOnly
    ? "view"
    : taggingMode;

  useEffect(() => {
    if (!isExperimentViewOnly) {
      return;
    }

    setTaggingMode("view");
    setTaggingForQuestionId(null);
  }, [isExperimentViewOnly]);

  return (
    <div className="flex h-full w-full flex-col bg-black pb-4 text-white">
      {syncError ? (
        <div className="px-4 pt-4">
          <div className="flex items-center justify-between rounded-xl border border-red-500/25 bg-red-500/10 px-4 py-3 text-sm text-red-100">
            <span className="pr-4">{syncError}</span>
            <button
              type="button"
              onClick={dismissSyncError}
              className="text-xs font-medium text-red-200 transition-colors hover:text-white"
            >
              Dismiss
            </button>
          </div>
        </div>
      ) : null}
      {generationError && (
        <div className="mx-4 mt-2 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <span className="flex-1">{generationError}</span>
          <button
            type="button"
            onClick={dismissGenerationError}
            className="ml-2 text-red-400 hover:text-red-600"
          >
            ✕
          </button>
        </div>
      )}
      <div className="flex flex-1 flex-col overflow-hidden px-4 pt-4">
        <main className="relative flex flex-1 gap-4 overflow-hidden">
          <div className="relative flex h-full flex-1 flex-col overflow-hidden rounded-xl border border-white/10 bg-[#0a0a0a] shadow-2xl">
            <div className="z-10 flex h-14 w-full items-center justify-between border-b border-white/10 bg-[#0F0F0F] px-4">
              <div className="flex flex-1 items-center gap-2">
                {isFeedbackFlowSyncing ? (
                  <span className="text-[11px] text-white/40">
                    Saving feedback flow...
                  </span>
                ) : null}
              </div>

              <div className="flex min-w-0 flex-[1.7] items-center justify-center px-3">
                {showTopRouteFlow && (
                  <div className="flex min-w-0 items-center gap-2">
                    <button
                      onClick={goToPreviousRoute}
                      disabled={activeRouteIndex <= 0}
                      className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/20 bg-white/5 text-white/80 transition-colors hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-30"
                      title="Previous route"
                    >
                      <ChevronLeft size={16} />
                    </button>

                    {feedbackFlowDraft.intro.enabled && (
                      <button
                        onClick={() => setActivePreview("intro")}
                        className={`flex h-9 w-9 items-center justify-center rounded-lg border transition-colors ${
                          activePreview === "intro"
                            ? "border-[#D9B64B] bg-[#D9B64B] text-black"
                            : "border-white/20 bg-white/5 text-white/80 hover:bg-white/10"
                        }`}
                        title="Intro"
                      >
                        <Play size={14} />
                      </button>
                    )}

                    <div className="no-scrollbar flex min-w-0 items-center gap-2 overflow-x-auto">
                      {isSitemapLoading && navigableRoutes.length === 0 ? (
                        <span className="px-2 text-[11px] text-white/50">
                          Loading sitemap routes...
                        </span>
                      ) : (
                        navigableRoutes.map((route, index) => {
                          const isActive =
                            activePreview === "product" && index === activeRouteIndex;
                          return (
                            <button
                              key={route}
                              onClick={() => goToRoute(route)}
                              className={`h-9 min-w-9 rounded-xl border px-3 text-lg font-semibold transition-colors ${
                                isActive
                                  ? "border-[#D9B64B] bg-[#D9B64B] text-black"
                                  : "border-[#2A6E45] bg-[rgba(28,85,51,0.45)] text-[#7EEEA4] hover:bg-[rgba(28,85,51,0.7)]"
                              }`}
                              title={route}
                            >
                              {index + 1}
                            </button>
                          );
                        })
                      )}
                    </div>

                    {feedbackFlowDraft.outro.enabled && (
                      <button
                        onClick={() => setActivePreview("outro")}
                        className={`flex h-9 w-9 items-center justify-center rounded-lg border transition-colors ${
                          activePreview === "outro"
                            ? "border-[#D9B64B] bg-[#D9B64B] text-black"
                            : "border-white/20 bg-white/5 text-white/80 hover:bg-white/10"
                        }`}
                        title="Outro"
                      >
                        <Flag size={14} />
                      </button>
                    )}

                    <button
                      onClick={goToNextRoute}
                      disabled={
                        activeRouteIndex < 0 ||
                        activeRouteIndex >= navigableRoutes.length - 1
                      }
                      className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/20 bg-white/5 text-white/80 transition-colors hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-30"
                      title="Next route"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                )}
              </div>

              <div className="flex flex-1 items-center justify-end gap-2">
                <div className="mr-2 flex items-center rounded-lg bg-white/5 p-0.5">
                  <button
                    onClick={() => setViewport("desktop")}
                    className={`flex h-7 w-8 items-center justify-center rounded-md transition-colors ${
                      viewport === "desktop"
                        ? "bg-white/10 text-white"
                        : "text-white/40 hover:text-white/80"
                    }`}
                    title="Desktop View"
                  >
                    <Monitor size={14} />
                  </button>
                  <button
                    onClick={() => setViewport("mobile")}
                    className={`flex h-7 w-8 items-center justify-center rounded-md transition-colors ${
                      viewport === "mobile"
                        ? "bg-white/10 text-white"
                        : "text-white/40 hover:text-white/80"
                    }`}
                    title="Mobile View"
                  >
                    <Smartphone size={14} />
                  </button>
                </div>

                <div className="mx-1 h-4 w-px bg-white/10" />

                <button
                  onClick={() => setIsParticipantPreviewOpen(true)}
                  disabled={!isPreviewReady}
                  className="rounded-lg border border-white/20 bg-white/5 px-3 py-1.5 text-xs font-medium text-white/80 transition-colors hover:bg-white/10 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
                  title="Open participant preview"
                >
                  Participant Preview
                </button>

                <button
                  onClick={handleRefresh}
                  className="flex h-8 w-8 items-center justify-center rounded-lg text-white/60 transition-colors hover:bg-white/10 hover:text-white"
                  title="Refresh Preview"
                >
                  <RefreshCw
                    size={14}
                    className={isRefreshing ? "animate-spin" : ""}
                  />
                </button>
                <button
                  onClick={openPreviewInNewTab}
                  disabled={!isPreviewReady}
                  className="flex h-8 w-8 items-center justify-center rounded-lg text-white/60 transition-colors hover:bg-white/10 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
                  title="Open preview in new tab"
                >
                  <ExternalLink size={14} />
                </button>
              </div>
            </div>

            <div className="relative flex flex-1 flex-col overflow-hidden bg-black">
              <div
                className={`flex h-full w-full items-center justify-center ${
                  viewport === "mobile" ? "p-4" : "p-0"
                }`}
              >
                <div
                  className={`relative flex h-full flex-col overflow-hidden transition-all duration-300 ease-in-out ${
                    viewport === "mobile"
                      ? "w-[375px] rounded-[2.5rem] border-[8px] border-black bg-black shadow-2xl ring-1 ring-white/20"
                      : "w-full bg-transparent"
                  }`}
                >
                  <div
                    className={`relative flex-1 overflow-hidden bg-white ${
                      viewport === "mobile" ? "rounded-[1.8rem]" : ""
                    }`}
                  >
                      {activePreview === "intro" && (
                        <IntroMockup
                          intro={feedbackFlowDraft.intro}
                          setActivePreview={setActivePreview}
                        />
                      )}
                      {activePreview === "outro" && (
                        <OutroMockup
                          outro={feedbackFlowDraft.outro}
                          setActivePreview={setActivePreview}
                        />
                      )}

                    <div
                      className={`absolute inset-0 ${
                        activePreview === "product"
                          ? "z-0 opacity-100"
                          : "pointer-events-none z-[-1] opacity-0"
                      }`}
                    >
                      {isPreviewReady ? (
                        <>
                          <iframe
                            key={`iframe-${previewBaseUrl}-${refreshKey}`}
                            ref={previewFrameRef}
                            src={buildExperimentPreviewUrl(
                              iframeRoute,
                              previewBaseUrl,
                            )}
                            className="absolute inset-0 h-full w-full border-0 bg-white"
                            title="Preview"
                          />

                          <AnimatePresence>
                            <motion.div
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                              className={`absolute inset-0 z-10 ${
                                effectiveTaggingMode === "view"
                                  ? "pointer-events-none"
                                  : ""
                              }`}
                            >
                              <TaggingAnnotationOverlay
                                onAnnotationCreate={handleAnnotationCreate}
                                isActive={activePreview === "product"}
                                previewFrameRef={previewFrameRef}
                                taggingMode={effectiveTaggingMode}
                              />
                            </motion.div>
                          </AnimatePresence>
                        </>
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center bg-[#050505] px-6 text-center">
                          <div className="max-w-sm">
                            <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-white/15 border-t-[#D9B64B]" />
                            <p className="mt-4 text-sm font-semibold text-white">
                              Deploying prototype for tagging
                            </p>
                            <p className="mt-2 text-xs leading-5 text-white/55">
                              The preview will appear here automatically once the
                              deployment status becomes deployed and the public URL is
                              ready.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {isFeedbackFlowInitializing ? (
                <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/70 backdrop-blur-sm">
                  <div className="text-center">
                    <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-white/15 border-t-[#D9B64B]" />
                    <p className="mt-4 text-sm font-semibold text-white">
                      Loading feedback flow
                    </p>
                    <p className="mt-2 text-xs text-white/55">
                      Pulling the latest intro, section, and question state from the
                      backend.
                    </p>
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ type: "spring", bounce: 0.15, duration: 0.3 }}
              className="relative h-full w-[320px] flex-shrink-0 overflow-hidden rounded-xl border border-white/10 shadow-2xl"
            >
              <div className={isExperimentViewOnly ? "h-full opacity-90" : "h-full"}>
                <TaggingQuestionsPanel
                  feedbackFlow={feedbackFlowDraft}
                  coreResearchQuestions={coreResearchQuestions}
                  updateIntro={updateIntro}
                  updateOutro={updateOutro}
                  saveQuestion={saveQuestion}
                  deleteQuestion={handleDeleteQuestion}
                  reorderQuestions={reorderQuestions}
                  addQuestion={addQuestion}
                  setSectionEnabled={setSectionEnabled}
                  onTagExistingQuestion={handleTagExistingQuestion}
                  previewFrameRef={previewFrameRef}
                  previewBaseUrl={previewBaseUrl}
                  iframeRoute={iframeRoute}
                  taggingMode={effectiveTaggingMode}
                  onTaggingModeChange={handleTaggingModeChange}
                  activePreview={activePreview}
                  setActivePreview={setActivePreview}
                  isInitializing={isTaggingPanelInitializing}
                  isSyncing={isFeedbackFlowSyncing}
                  isGenerating={isGenerating}
                  generationError={generationError}
                  dismissGenerationError={dismissGenerationError}
                  isPreviewReady={isPreviewReady}
                  isReadOnly={isExperimentViewOnly}
                />
              </div>

              {isExperimentViewOnly ? (
                <div className="pointer-events-none absolute left-1/2 top-4 z-10 flex w-[calc(100%-2rem)] -translate-x-1/2 justify-center">
                  <div className="max-w-xs rounded-2xl border border-amber-500/25 bg-[#111111] px-4 py-3 text-sm text-amber-100 shadow-xl">
                    {readOnlyMessage}
                  </div>
                </div>
              ) : null}
            </motion.div>
          </AnimatePresence>

        </main>
      </div>

      <AnimatePresence>
        {isParticipantPreviewOpen && (
          <PersistedExperimentParticipantPreview
            experimentId={experimentId}
            initialRoute={iframeRoute}
            mode="embedded"
            onBack={() => setIsParticipantPreviewOpen(false)}
            onFinish={handleParticipantPreviewFinish}
          />
        )}
      </AnimatePresence>
    </div>
  );
};
